import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import LoadingBox from "../../../components/LoadingBox";
import { getCalendar, setCalendar, useCalendar } from "../../../contexts/CalendarContext";
import { datetime } from "../../../util/library/datetime";
import { SiteService } from "../../../util/services/site";
import { selectOneSetting, selectOverview } from "../../../util/store/selectors";
import { getStore, useSelector } from "../../../util/store/store";
import { aDayMillisecond, aWeekMillisecond } from "../../../util/varibles/constants";
import { addClassName, clearSelect, removeClassName, showErrorResponse } from "../../../util/varibles/global";
import { IOperation } from "../../../util/varibles/interface";
import { OverviewSetting } from "../../../util/varibles/userSetting";
import { CALENDAR_ID, orderBySavedIds } from "../constants";
import { overviewActions } from "../reducer";
import useTimelineZoom from "../useTimelineZoom";
import HeaderCalendarSite from "./HeaderCalendarSite";
import Operations from "./Operations";
import Sites from "./Sites";
import styles from "./style.module.scss";

export const getDayId = (time: number) => `day-${datetime(time).format("DD/MM/YYYY")}`;


const SizeCalendar: React.FC<{ calendarRef: () => HTMLDivElement | null }> = ({ calendarRef }) => {
    const [startTime] = useCalendar(state => state.startTime);
    const [finishTime] = useCalendar(state => state.finishTime);
    const [widthDay, set] = useCalendar(state => state.widthDay);
    const weekPerPage = useSelector(selectOneSetting(OverviewSetting.WEEK_PER_PAGE));
    const siteCount = useSelector(selectOverview.sites).length;

    useEffect(() => {
        const el = calendarRef();
        if (el) {
            el.style.setProperty('--calendar-left', String(startTime));
            el.style.setProperty('--calendar-width', String(finishTime - startTime));
        }
    }, [calendarRef, startTime, finishTime])

    useEffect(() => {
        const el = calendarRef();
        if (el) {
            el.style.setProperty('--width-day', `${widthDay}px`);
        }
    }, [calendarRef, widthDay])

    useEffect(() => {
        const el = calendarRef();
        if (el) {
            const { offsetWidth, scrollLeft } = el;
            const day = Math.round(Math.max(scrollLeft, widthDay) / widthDay);
            const width = (offsetWidth - 160) / (7 * Math.max(weekPerPage || 1, 1));
            set({ widthDay: width });
            setTimeout(() => {
                el.scrollLeft = day * width;
            }, 100)
        }
    }, [calendarRef, set, weekPerPage, widthDay])

    useEffect(() => {
        const el = calendarRef();
        if (!el)
            return;

        const updateSiteHeight = () => {
            const availableHeight = el.clientHeight - 67;
            const minHeight = siteCount > 0
                ? Math.max(Math.floor(availableHeight / siteCount), 120)
                : 120;
            set({ minHeight });
        };

        updateSiteHeight();
        const resizeObserver = new ResizeObserver(updateSiteHeight);
        resizeObserver.observe(el);

        return () => resizeObserver.disconnect();
    }, [calendarRef, set, siteCount]);

    return null;
}

const Loading: React.FC = () => {
    const [loadingMode] = useCalendar(state => state.loadingMode);
    const [loading] = useCalendar(state => state.loading);

    useEffect(() => {
        const calendar = document.getElementById(CALENDAR_ID);
        if (!calendar)
            return;

        if (loading) {
            calendar.setAttribute('data-lazy-loading', 'true');
            return;
        }

        let secondFrame = 0;
        const firstFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => {
                calendar.removeAttribute('data-lazy-loading');
            });
        });

        return () => {
            cancelAnimationFrame(firstFrame);
            cancelAnimationFrame(secondFrame);
        };
    }, [loading]);

    return loading ? <>
        {(loadingMode === 'left') && <div className={styles.loading} data-position='left'>
            <LoadingBox loading={true} />
        </div>}
        {loadingMode === "center" && <div className={styles.loading} data-position='center'>
            <LoadingBox loading={true} />
        </div>}
        {(loadingMode === 'right') && <div className={styles.loading} data-position='right'>
            <LoadingBox loading={true} />
        </div>}
    </> : null;
};

const Days = () => {
    const [dragging, setDragging] = useState(false);
    const pageX = useRef(0);
    const pageY = useRef(0);
    const scrollLeft = useRef(0);
    const scrollTop = useRef(0);
    const [startTime] = useCalendar(state => state.startTime);
    const [finishTime] = useCalendar(state => state.finishTime);
    const currentTime = datetime().format("DD/MM/YYYY");

    const days = useMemo(() => {
        const result = [];
        for (let time = startTime; time <= finishTime; time += aDayMillisecond) {
            result.push(time);
        }
        return result;
    }, [startTime, finishTime]);

    const handleMouseMove = useCallback((e: any) => {
        const el = document.getElementById(CALENDAR_ID);
        if (el) {
            el.scrollLeft = scrollLeft.current - e.pageX + pageX.current;
            el.scrollTop = scrollTop.current - e.pageY + pageY.current;
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        setDragging(false);
        removeClassName(document.body);
        clearSelect();
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove])

    useEffect(() => {
        return handleMouseUp;
    }, [handleMouseUp]);

    const handleMouseDown = useCallback((e: any) => {
        if (e.button !== 0)
            return;

        pageX.current = e.pageX;
        pageY.current = e.pageY;
        const el = document.getElementById(CALENDAR_ID);
        if (el) {
            scrollLeft.current = el.scrollLeft;
            scrollTop.current = el.scrollTop;
        }
        setDragging(true);
        addClassName(document.body);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove, handleMouseUp]);

    return <div className={styles.days}>
        <Operations />
        {days.map(time => <div key={time} className={styles.dayContainer}>
            <div id={getDayId(time)} className={styles.day} data-current={currentTime === datetime(time).format("DD/MM/YYYY")}>
                <div>
                    {datetime(time).format('ddd DD').split(' ').map((item: string) => <span key={item}>{item}</span>)}
                </div>
            </div>
            <div className={styles.dayLine} data-last={datetime(time).day === 6} data-dragging={dragging} onMouseDown={handleMouseDown} />
        </div>)}
    </div>
}

const CalendarSite: React.FC = () => {
    const get = getCalendar();
    const set = setCalendar();
    const [startTime] = useCalendar(state => state.startTime);
    const [finishTime] = useCalendar(state => state.finishTime);
    const [widthDay] = useCalendar(state => state.widthDay);
    const dispatch = useDispatch();

    const calendarRef = useRef<HTMLDivElement>(null);
    useTimelineZoom(calendarRef);
    const scrollAnchor = useRef<{ dayId: string, offset: number } | null>(null);

    const onLoad = useCallback((startTime: number, finishTime: number, mode: "left" | "right" | "center" = "center") => {
        set({ loading: true, loadingMode: mode });
        SiteService.history({ start_time: startTime, finish_time: finishTime })
            .then(data => {
                const { operations, sites, before_count, after_count } = data;
                let secondProps = {}, rootProps = {};
                if (mode === 'center') {
                    rootProps = { sites };
                    secondProps = { before: before_count, after: after_count };
                } else if (mode === 'left') {
                    secondProps = { before: before_count, startTime };
                } else if (mode === 'right') {
                    secondProps = { after: after_count, finishTime };
                }
                const siteOrder = selectOneSetting(OverviewSetting.SITE_ORDER)(getStore());
                dispatch(overviewActions.updateOpsSite({
                    ...rootProps,
                    ...(mode === 'center' ? { sites: orderBySavedIds(sites, siteOrder) } : {}),
                    opsSite: operations.reduce((rs: { [id: string]: IOperation }, item) => {
                        rs[item.operation.id] = item;
                        return rs;
                    }, {})
                }))
                set({ ...secondProps, loading: false, })
            })
            .catch(error => {
                scrollAnchor.current = null;
                showErrorResponse('Get data failure', error);
                set({ loading: false });
            })
    }, [dispatch, set])

    useLayoutEffect(() => {
        const anchor = scrollAnchor.current;
        const calendar = calendarRef.current;
        if (!anchor || !calendar)
            return;

        const dayElement = document.getElementById(anchor.dayId);
        if (dayElement) {
            const nextOffset = dayElement.getBoundingClientRect().left - calendar.getBoundingClientRect().left;
            calendar.scrollLeft += nextOffset - anchor.offset;
        }
        scrollAnchor.current = null;
    }, [startTime]);

    useEffect(() => {
        setTimeout(() => {
            const time = datetime().startOf('day').time;
            const element = document.getElementById(getDayId(time));
            if (element) {
                const { offsetLeft } = element;
                const calendar = document.getElementById(CALENDAR_ID);
                if (calendar)
                    calendar.scrollLeft = offsetLeft;
            }
        }, 300)
    }, []);

    useEffect(() => {
        const { startTime, finishTime } = get();
        onLoad(startTime, finishTime);
    }, [get, onLoad])

    const onScroll = useCallback(() => {
        const { loading, before, after, startTime, finishTime } = get();
        if (loading || !calendarRef.current)
            return;
        const { scrollLeft, scrollWidth, offsetWidth } = calendarRef.current;
        if (scrollLeft <= 1) {
            const oldStart = startTime;
            const start = oldStart - (3 * aWeekMillisecond);
            const firstDay = document.getElementById(getDayId(oldStart));
            if (firstDay) {
                scrollAnchor.current = {
                    dayId: getDayId(oldStart),
                    offset: firstDay.getBoundingClientRect().left - calendarRef.current.getBoundingClientRect().left,
                };
            }
            if (before)
                onLoad(start, oldStart - 1, 'left');
            else
                set({ startTime: start });
        } else if (scrollLeft + offsetWidth >= scrollWidth - 1) {
            const start = finishTime + 1;
            const finish = finishTime + (3 * aWeekMillisecond);
            if (after)
                onLoad(start, finish, 'right');
            else
                set({ finishTime: finish });
        }
    }, [get, onLoad, set]);

    useEffect(() => {
        const el = document.getElementById(CALENDAR_ID);
        if (el)
            el.addEventListener('scroll', onScroll);
        return () => {
            if (el)
                el.removeEventListener('scroll', onScroll);
        }
    }, [onScroll]);

    return <div className={styles.screen}>
        <HeaderCalendarSite />
        <div id={CALENDAR_ID} ref={calendarRef} className={styles.calendar} style={{ "--width-day": `${widthDay}px`, "--calendar-left": startTime, "--calendar-width": finishTime - startTime } as any}>
            <SizeCalendar calendarRef={() => calendarRef.current} />
            <Sites />
            <Days />
            <Loading />
        </div>
    </div>
}

export default CalendarSite;
